import React, { useEffect, useState, useCallback } from "react";
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  Image,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Alert,
} from "react-native";
import Header from "../components/Header";
import LoadingButton from "../components/LoadingButton";
import {
  getChargerQRCode,
  emailChargerQRCode,
} from "../services/charger-service";
import { isSuccessful } from "../services/api-requests";

interface ChargerQRCodeProps {
  navigation: any;
  route: any;
}

const ChargerQRCode: React.FC<ChargerQRCodeProps> = ({ navigation, route }) => {
  const { chargerId, chargerName } = route.params ?? {};

  const [qrCodeUri, setQrCodeUri] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  // Fetch the QR code image from the backend
  const fetchQRCode = useCallback(async () => {
    if (!chargerId) {
      setError("No charger ID provided.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getChargerQRCode(chargerId);
      if (isSuccessful(response.status) && response.payload?.qrCodeBase64) {
        setQrCodeUri(response.payload.qrCodeBase64);
      } else {
        setError("Unable to load QR code. Please try again.");
      }
    } catch (err) {
      console.error("Error fetching QR code:", err);
      setError("An error occurred while loading the QR code.");
    } finally {
      setLoading(false);
    }
  }, [chargerId]);

  useEffect(() => {
    fetchQRCode();
  }, [fetchQRCode]);

  // Handle sending QR code via email
  const handleEmailQRCode = async () => {
    if (!chargerId) return;

    setEmailSending(true);
    setEmailSent(false);

    try {
      const response = await emailChargerQRCode(chargerId);
      if (isSuccessful(response.status)) {
        setEmailSent(true);
        if (Platform.OS === "web") {
          // On web, just show the success state via the button text
        } else {
          Alert.alert(
            "QR Code Sent",
            "The QR code has been sent to your email address.",
            [{ text: "OK" }]
          );
        }
      } else {
        const message = "Failed to send QR code email. Please try again.";
        if (Platform.OS === "web") {
          setError(message);
        } else {
          Alert.alert("Error", message, [{ text: "OK" }]);
        }
      }
    } catch (err) {
      console.error("Error emailing QR code:", err);
      const message = "An error occurred while sending the email.";
      if (Platform.OS === "web") {
        setError(message);
      } else {
        Alert.alert("Error", message, [{ text: "OK" }]);
      }
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.container}>
        {Platform.OS !== "web" && (
          <Header headerText="Charger QR Code" />
        )}

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Charger name */}
          {chargerName ? (
            <Text style={styles.chargerName}>{chargerName}</Text>
          ) : null}

          <Text style={styles.description}>
            Drivers can scan this QR code to find and charge at your station.
          </Text>

          {/* QR Code display area */}
          <View style={styles.qrContainer}>
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#51B868" />
                <Text style={styles.loadingText}>Loading QR Code...</Text>
              </View>
            ) : error ? (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>{error}</Text>
                <LoadingButton
                  onPress={fetchQRCode}
                  backgroundColor="#39BEB7"
                  style={{ marginTop: 16 }}
                >
                  Try Again
                </LoadingButton>
              </View>
            ) : qrCodeUri ? (
              <View style={styles.qrImageWrapper}>
                <Image
                  source={{ uri: qrCodeUri }}
                  style={styles.qrImage}
                  resizeMode="contain"
                  alt="Charger QR Code"
                />
              </View>
            ) : null}
          </View>

          {/* Email QR Code button */}
          {!loading && qrCodeUri && (
            <View style={styles.buttonContainer}>
              <LoadingButton
                onPress={handleEmailQRCode}
                backgroundColor={emailSent ? "#51B868" : "#39BEB7"}
                isLoading={emailSending}
                hapticsOnStart
                hapticsOnSuccess
              >
                {emailSent ? "QR Code Sent!" : "Email this QR code to me"}
              </LoadingButton>

              {emailSent && (
                <Text style={styles.emailSentHint}>
                  Check your email inbox for the QR code.
                </Text>
              )}
            </View>
          )}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#0B1E1D",
  },
  container: {
    flex: 1,
    backgroundColor: "#0B1E1D",
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingBottom: 40,
    alignItems: "center",
  },
  chargerName: {
    fontSize: 22,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    marginTop: 16,
    marginBottom: 4,
  },
  description: {
    fontSize: 15,
    color: "#A0AEC0",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 24,
    lineHeight: 22,
    paddingHorizontal: 16,
  },
  qrContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 300,
  },
  loadingContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  loadingText: {
    color: "#A0AEC0",
    fontSize: 14,
    marginTop: 12,
  },
  errorContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  errorText: {
    color: "#E53E3E",
    fontSize: 15,
    textAlign: "center",
    lineHeight: 22,
  },
  qrImageWrapper: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  qrImage: {
    width: 260,
    height: 260,
  },
  buttonContainer: {
    width: "100%",
    marginTop: 32,
    alignItems: "center",
  },
  emailSentHint: {
    color: "#51B868",
    fontSize: 13,
    marginTop: 8,
    textAlign: "center",
  },
});

export default ChargerQRCode;
